import React from "react";

const Bar = ({ className = "" }) => (
  <div aria-hidden="true" className={`rounded-full ${className}`} />
);

const BrowserDots = () => (
  <div className="flex gap-1" aria-hidden="true">
    <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
    <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
    <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
  </div>
);

export const Laptop = ({ className = "" }) => (
  <div className={className} role="img" aria-label="Notebook exibindo um website moderno desenvolvido pela EDALIX">
    <div className="rounded-xl border border-white/15 bg-[#0C0C16] p-1.5 shadow-[0_40px_90px_-30px_rgba(0,229,255,0.35)]">
      <div className="relative aspect-[16/10] overflow-hidden rounded-lg bg-[#07070E]">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#8A2BE2]/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-[#00E5FF]/15 blur-3xl" />
        <div className="relative flex items-center gap-2 border-b border-white/10 px-3 py-2">
          <BrowserDots />
          <div className="ml-1 flex h-4 flex-1 items-center rounded-full bg-white/5 px-2.5">
            <span className="text-[7px] tracking-[0.2em] text-white/40">edalix.com.br</span>
          </div>
        </div>
        <div className="relative grid grid-cols-2 gap-3 p-3.5">
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-md bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2]" />
              <Bar className="h-1.5 w-10 bg-white/15" />
            </div>
            <Bar className="h-3 w-full bg-white/25" />
            <Bar className="h-3 w-4/5 bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]" />
            <Bar className="h-1.5 w-3/4 bg-white/10" />
            <Bar className="h-1.5 w-2/3 bg-white/10" />
            <div className="flex gap-1.5 pt-1.5">
              <div className="h-4 w-12 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]" />
              <div className="h-4 w-12 rounded-full border border-white/20" />
            </div>
          </div>
          <div className="flex items-end justify-center gap-1.5 pb-1">
            {[35, 58, 44, 72, 60, 88].map((h, i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{ height: `${h}%` }}
                className="w-3 rounded-sm bg-gradient-to-t from-[#00E5FF]/70 to-[#8A2BE2]/70"
              />
            ))}
          </div>
          <div className="col-span-2 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5 rounded-md border border-white/10 bg-white/[0.04] p-2">
                <Bar className="h-1.5 w-2/3 bg-white/20" />
                <Bar className="h-1 w-full bg-white/10" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="mx-auto h-2 w-[104%] -translate-x-[2%] rounded-b-2xl bg-gradient-to-b from-[#23233A] to-[#0B0B14]" />
  </div>
);

export const Tablet = ({ className = "" }) => (
  <div className={className} role="img" aria-label="Tablet exibindo um dashboard de gestão desenvolvido pela EDALIX">
    <div className="rounded-2xl border border-white/15 bg-[#0C0C16] p-1.5 shadow-[0_30px_70px_-25px_rgba(138,43,226,0.45)]">
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-[#07070E]">
        <div className="flex h-full">
          <div className="flex w-8 flex-col items-center gap-2.5 border-r border-white/10 py-3">
            <span className="h-3 w-3 rounded-md bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2]" />
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-white/20" />
            ))}
          </div>
          <div className="flex-1 p-2.5">
            <Bar className="h-2 w-16 bg-white/20" />
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-md border border-white/10 bg-white/[0.04] p-1.5">
                  <Bar className="h-1 w-2/3 bg-white/15" />
                  <Bar className="mt-1.5 h-1.5 w-1/2 bg-gradient-to-r from-[#00E5FF]/80 to-[#8A2BE2]/80" />
                </div>
              ))}
            </div>
            <div className="mt-2 flex h-[52%] items-end gap-1 rounded-md border border-white/10 bg-white/[0.03] p-1.5">
              {[40, 65, 50, 85, 60, 75, 55, 90].map((h, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  style={{ height: `${h}%` }}
                  className="flex-1 rounded-sm bg-gradient-to-t from-[#8A2BE2]/60 to-[#00E5FF]/70"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const Phone = ({ className = "" }) => (
  <div className={className} role="img" aria-label="Smartphone exibindo um aplicativo desenvolvido pela EDALIX">
    <div className="rounded-[1.7rem] border border-white/15 bg-[#0C0C16] p-1 shadow-[0_30px_60px_-20px_rgba(0,229,255,0.4)]">
      <div className="relative aspect-[9/19] overflow-hidden rounded-[1.35rem] bg-[#07070E]">
        <div className="mx-auto mt-1.5 h-3 w-12 rounded-full border border-white/10 bg-black/80" />
        <div className="space-y-2 p-2.5">
          <div className="flex items-center justify-between pt-1">
            <Bar className="h-1.5 w-10 bg-white/20" />
            <span className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-[#00E5FF]/70 to-[#8A2BE2]/70" />
          </div>
          <div className="rounded-lg bg-gradient-to-br from-[#00E5FF]/25 to-[#8A2BE2]/30 p-2.5">
            <Bar className="h-1 w-1/2 bg-white/30" />
            <Bar className="mt-2 h-2.5 w-3/4 bg-white/50" />
            <div className="mt-2.5 h-3.5 w-12 rounded-full bg-white/90" />
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] p-2">
              <span className="h-5 w-5 rounded-md bg-white/10" />
              <div className="flex-1 space-y-1">
                <Bar className="h-1.5 w-3/4 bg-white/20" />
                <Bar className="h-1 w-1/2 bg-white/10" />
              </div>
            </div>
          ))}
        </div>
        <div className="absolute inset-x-3 bottom-2 flex items-center justify-around rounded-full border border-white/10 bg-[#0B0B16]/90 py-2 backdrop-blur">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full ${i === 0 ? "bg-[#00E5FF]" : "bg-white/25"}`}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);
